import React, { createContext, useState, useContext, useEffect, useRef } from "react";
import { ToastContainer } from "react-toastify";
import axios from "axios";

import { useTemplateAutosave } from "../../../shared/useTemplateAutosave";
import { recipientCountKey } from "./recipientCountKey";
import { parseLiquid } from "../BeefreeTemplates/ErrorsContainer";
import BroadcastEdit from "./BroadcastEdit";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import { offsetDateFromTimeZone, offsetDateToTimezone } from "../../../shared/timeZoneLogic";

const moment = require("moment-timezone");

const BroadcastEditContext = createContext();

export function useBroadcastEditContext() {
  return useContext(BroadcastEditContext);
}

export const BroadcastEditProvider = (props) => {
  const {
    csrfToken,
    user,
    team,
    venueOwnership,
    beefreeToken,
    sendDomain,
    availableSendDomains,
    defaultSendDomainId,
    previouslySent,
    sentAt,
    overviewMetrics,
    overviewLinkClicks,
    ahoyMessagesDataTableProps,
    twilioMessagingServices,
    sampleTwilioData,
    emailMergeTags,
    twilioMergeTags,
    twilioEventMergeTags,
    smsStatus,
    smsStarterTexts,
    smsStarterPlaceholders,
    readOnly = false,
  } = props;
  const [broadcast, setBroadcast] = useState(props.broadcast);

  const tabParam = new URLSearchParams(window.location.search).get('tab');

  const isEmailTemplate = !broadcast.twilio_template;
  const beefreeTemplateId = broadcast.beefree_template?.id;

  const [subject, setSubject] = useState(broadcast.subject);
  const [preheader, setPreheader] = useState(broadcast.preheader);
  const [template, setTemplate] = useState(broadcast.beefree_template?.template);
  const [processedTemplate, setProcessedTemplate] = useState(broadcast.beefree_template?.template);
  const [beefreeTemplate, setBeefreeTemplate] = useState(broadcast.beefree_template);
  const [twilioTemplate, setTwilioTemplate] = useState(broadcast.twilio_template);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [scheduledSend, setScheduledSend] = useState(broadcast.pending_scheduled_send);
  const [errors, setErrors] = useState([]);
  const [sampleFanId, setSampleFanId] = useState(props.sampleFanId);
  const [sampleData, setSampleData] = useState({fan: {}});
  const [emailSendDomain, setEmailSendDomain] = useState(broadcast.beefree_template?.email_send_domain);
  const [selectedSendDomain, setSelectedSendDomain] = useState(sendDomain);
  const [scheduledSendPaneOpen, setScheduledSendPaneOpen] = useState(false);
  const [timeZone, setTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [previousBroadcastTemplates, setPreviousBroadcastTemplates] = useState([]);
  const [adminBlueprints, setAdminBlueprints] = useState([]);
  const [teamBlueprints, setTeamBlueprints] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [recipientsCount, setRecipientsCount] = useState("--");
  const [templateHasContent, setTemplateHasContent] = useState(null);
  const [scheduledAt, setScheduledAt] = useState(
    scheduledSend ? offsetDateToTimezone(scheduledSend.scheduled_at, timeZone) : null
  );
  const [segmentFilters, setSegmentFilters] = useState(broadcast.parent_segment_filter ? (
    [
      {
        ...broadcast.parent_segment_filter,
        set: true
      },
      ...broadcast.parent_segment_filter.children.map(childFilter => ({
        ...childFilter,
        set: true
      })),
    ]
  ) : []);
  const [segment, setSegment] = useState(broadcast.segment || {});
  const [fanFilterGroupId, setFanFilterGroupId] = useState(segment.fan_filter_group?.id || 0);
  const [showTwilioComposer, setShowTwilioComposer] = useState(false);

  const canShowOverview = previouslySent && isEmailTemplate;
  const requestedTab = tabParam === "overview" && !canShowOverview ? null : tabParam;

  const initialTab = requestedTab ? (
    requestedTab
  ) : canShowOverview ? (
    "overview"
  ) : scheduledSend || previouslySent ? (
    isEmailTemplate ? "content" : "twilio-editor"
  ) : "recipients";

  const [tab, setTab] = useState(initialTab);

  const visibleSegmentFilters = segmentFilters.filter(filter => !filter._destroy);

  useEffect(() => {
    if (!!sampleFanId) {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      axios.get(`/teams/${team.id}/broadcasts/${broadcast.id}/sample_event_data`, {
        params: {
          fan_id: sampleFanId,
        }
      })
        .then(({ data }) => setSampleData(data))
        .catch((_error) => toastError("Could not fetch sample data, please try again"));
    }
  }, [sampleFanId]);

  useEffect(() => {
    if (readOnly) return;

    fetchRecipientCount();
  }, [recipientCountKey(broadcast), segmentFilters])

  useEffect(() => {
    if (broadcast.segment) {
      setSegment(broadcast.segment);

      if (broadcast.segment.fan_filter_group) {
        setFanFilterGroupId(broadcast.segment.fan_filter_group.id);
      }
    }
  }, [broadcast.segment])

  useEffect(() => {
    setTwilioTemplate(broadcast.twilio_template);
  }, [broadcast.twilio_template])

  useEffect(() => {
    setEmailSendDomain(broadcast.beefree_template?.email_send_domain);
  }, [broadcast.beefree_template])

  const broadcastSaves = useRef(Promise.resolve());

  const updateBroadcast = (payload, alert = true, callback = null) => {
    // Kill any queued/in-flight autosave first so it can't land after this
    // manual write and revert it.
    cancelAutosave();
    const request = broadcastSaves.current.then(() => {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      return axios.put(`/teams/${team.id}/broadcasts/${broadcast.id}`, payload)
        .then((data) => {
          setBroadcast(data.data);

          if (alert) {
            toastSuccess("Broadcast updated successfully");
          }

          if (callback) {
            callback();
          }
        })
        .catch(() => toastError("Could not update broadcast, please try again"));
    });

    broadcastSaves.current = request;
    return request;
  };

  const waitForBroadcastSaves = () => broadcastSaves.current;

  // Autosave the email template. Mirrors the manual Save button's payload
  // (beefree_template_attributes: { id, template }) but stays SILENT: no
  // success toast, no navigation, and crucially NO setBroadcast() from the
  // response — the editor already holds the newest content (a round-trip could
  // stomp in-flight keystrokes), the beefree_template.id is stable across
  // updates so the manual Save button stays valid, and refreshing the whole
  // record on every ~1.2s autosave would needlessly re-fire recipient-count /
  // send-domain effects mid-composition. Guarded on beefree_template so a
  // template-less broadcast can never NPE here. `isAutosaving` drives the
  // header Save button's "Saving…" state.
  const { isAutosaving, autosave: autosaveTemplate, cancel: cancelAutosave } = useTemplateAutosave((templateData, cancelToken) => {
    if (!broadcast.beefree_template?.id) return Promise.resolve();

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    return axios.put(`/teams/${team.id}/broadcasts/${broadcast.id}`, {
      broadcast: {
        beefree_template_attributes: {
          id: broadcast.beefree_template.id,
          template: templateData,
        }
      }
    }, { cancelToken });
  });

  const createSegmentFilter = (segmentFilters, alert = true) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    const parentFilter = segmentFilters.slice(0, 1).find(filter => filter.set);
    const children = segmentFilters.slice(1).filter(filter => filter.set);

    return axios.post(`/teams/${team.id}/broadcasts/${broadcast.id}/segment_filters`, {
      segment_filter: {
        inclusion: parentFilter.inclusion,
        include_refunded_buyers: parentFilter.include_refunded_buyers || false,
        segment_ids: parentFilter.segments.map(segment => segment.id),
        children_attributes: children.map(childFilter => ({
          inclusion: childFilter.inclusion,
          segment_ids: childFilter.segments.map(segment => segment.id),
        }))
      }
    })
      .then(({ data }) => {
        if (alert) toastSuccess("Recipients saved successfully");

        setSegmentFilters(segmentFilters.map((filter, i) => {
          if (i === 0) {
            return {
              ...filter,
              id: data.id,
              set: true,
            };
          } else {
            return {
              ...filter,
              id: data.children[i - 1].id,
              set: true,
            };
          }
        }));
      })
      .catch(() => toastError("Could not save recipients, please try again"));
  };

  const updateSegmentFilter = (segmentFilters, alert = true) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    const parentFilter = segmentFilters.slice(0, 1).find(filter => filter.set);
    const children = segmentFilters.slice(1).filter(filter => filter.set);

    return axios.put(`/teams/${team.id}/broadcasts/${broadcast.id}/segment_filters/${parentFilter.id}`, {
      segment_filter: {
        id: parentFilter.id,
        inclusion: parentFilter.inclusion,
        include_refunded_buyers: parentFilter.include_refunded_buyers || false,
        segment_ids: parentFilter.segments.map(segment => segment.id),
        children_attributes: children.map(childFilter => ({
          id: childFilter.id,
          inclusion: childFilter.inclusion,
          segment_ids: childFilter.segments.map(segment => segment.id),
          _destroy: childFilter._destroy || false,
        }))
      }
    })
      .then(({ data }) => {
        if (alert) toastSuccess("Recipients updated successfully");

        setSegmentFilters(segmentFilters.map((filter, i) => {
          if (i === 0) {
            return {
              ...filter,
              id: data.id,
              set: true,
            };
          } else {
            return {
              ...filter,
              id: data.children[i - 1].id,
              set: true,
            };
          }
        }));
      })
      .catch(() => toastError("Could not save recipients, please try again"));
  };

  const destroySegmentFilter = (segmentFilter, { withChildren = false } = {}) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    return axios.delete(`/teams/${team.id}/broadcasts/${broadcast.id}/segment_filters/${segmentFilter.id}`, {
      params: withChildren ? { with_children: true } : {},
    })
      .then(() => {
        setSegmentFilters(withChildren ? [] : segmentFilters.filter(filter => filter.id !== segmentFilter.id));
      })
      .catch(error => toastError(error.response.data.errors.join(", ")));
  };

  const recipientCountRequest = useRef(0);

  const fetchRecipientCount = () => {
    const request = ++recipientCountRequest.current;
    const isLatest = () => request === recipientCountRequest.current;

    setRecipientsCount("--");
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.get(`/teams/${team.id}/broadcasts/${broadcast.id}/recipient_count`)
      .then(({ data }) => {
        if (isLatest()) setRecipientsCount(data.count);
      })
      .catch(() => {
        if (isLatest()) toastError("Could not determine recipient count, please try again");
      });
  };

  const fetchPreviousBroadcastTemplates = () => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.get(`/teams/${team.id}/beefree_templates.json`, {
      params: {
        q: {
          templatable_type_eq: 'Broadcast',
        }
      }
    })
      .then(({ data }) => {
        setPreviousBroadcastTemplates(data);
      })
      .catch(() => toastError("Could not load templates, please try again"));
  }

  const fetchAdminBlueprints = () => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.get(`/teams/${team.id}/beefree_templates/admin_blueprints.json`)
      .then(({ data }) => {
        setAdminBlueprints(data);
      })
      .catch(() => toastError("Could not load admin blueprints, please try again"));
  }

  const fetchTeamBlueprints = () => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.get(`/teams/${team.id}/beefree_templates/team_blueprints.json`)
      .then(({ data }) => {
        setTeamBlueprints(data);
      })
      .catch(() => toastError("Could not load team blueprints, please try again"));
  }

  const createBlueprint = (name, templateData, callback) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    // First save the current template
    axios.put(`/teams/${team.id}/broadcasts/${broadcast.id}`, {
      broadcast: {
        beefree_template_attributes: {
          id: broadcast.beefree_template.id,
          template: templateData,
        }
      }
    })
      .then(() => {
        // Then create the blueprint
        return axios.post(`/teams/${team.id}/beefree_templates.json`, {
          beefree_template: {
            name,
            template: templateData,
          }
        });
      })
      .then(({ data: blueprintData }) => {
        // Then update the beefree_template with copied_from_blueprint_id
        return axios.put(`/teams/${team.id}/broadcasts/${broadcast.id}`, {
          broadcast: {
            beefree_template_attributes: {
              id: broadcast.beefree_template.id,
              copied_from_blueprint_id: blueprintData.id,
            }
          }
        }).then(({ data: broadcastData }) => {
          setBroadcast(broadcastData);
          toastSuccess("Blueprint created successfully");
          fetchTeamBlueprints();
          if (callback) callback(blueprintData);
        });
      })
      .catch((error) => {
        const errors = error.response?.data?.errors || ["Could not create blueprint"];
        toastError(errors.join(", "));
        if (callback) callback(null);
      });
  }

  const updateBlueprint = (blueprintId, templateData, callback) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    // First save the current template
    axios.put(`/teams/${team.id}/broadcasts/${broadcast.id}`, {
      broadcast: {
        beefree_template_attributes: {
          id: broadcast.beefree_template.id,
          template: templateData,
        }
      }
    })
      .then(({ data: broadcastData }) => {
        setBroadcast(broadcastData);
        // Then update the blueprint
        return axios.put(`/teams/${team.id}/beefree_templates/${blueprintId}.json`, {
          beefree_template: {
            template: templateData,
          }
        });
      })
      .then(({ data }) => {
        toastSuccess("Blueprint updated successfully");
        fetchTeamBlueprints();
        if (callback) callback(data);
      })
      .catch((error) => {
        const errors = error.response?.data?.errors || ["Could not update blueprint"];
        toastError(errors.join(", "));
        if (callback) callback(null);
      });
  }

  const checkTemplateHasContent = () => {
    if (isEmailTemplate) {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

      axios.get(`/teams/${team.id}/broadcasts/${broadcast.id}/template_has_content`)
        .then(({ data }) => {
          setTemplateHasContent(data.has_content);
        })
        .catch(() => toastError("Could not check template content, please try again"));
    } else {
      setTemplateHasContent(broadcast.twilio_template?.template?.length ? true : false);
    }
  }

  const scheduleBroadcast = (callback, at = scheduledAt) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    const axiosRequest = scheduledSend ?
      axios.put(`/teams/${team.id}/scheduled_sends/${scheduledSend.id}`, {
        scheduled_send: {
          scheduled_at: offsetDateFromTimeZone(at, timeZone),
        }
      }) :
      axios.post(`/teams/${team.id}/scheduled_sends`, {
        scheduled_send: {
          schedulable_id: broadcast.id,
          schedulable_type: "Broadcast",
          scheduled_at: offsetDateFromTimeZone(at, timeZone),
        }
      });

      axiosRequest
        .then(({ data }) => {
          setScheduledSend(data);

          if(callback) {
            callback();
          }
        })
        .catch((error) => {
          setScheduledAt(scheduledSend ? offsetDateToTimezone(scheduledSend.scheduled_at, timeZone) : null);
          const errors = error.response.data;
          toastError(errors.join(", "));
        });
  };

  const formatDate = (date) => {
    const timezoneAbbr = moment.tz(timeZone).format("z");
    return `${moment(offsetDateToTimezone(date, timeZone)).format(`MMM D, YYYY - h:mma`)} ${timezoneAbbr}`;
  };

  const value = {
    defaultSendDomainId,
    csrfToken,
    user,
    team,
    venueOwnership,
    broadcast,
    setBroadcast,
    beefreeToken,
    isEmailTemplate,
    beefreeTemplateId,
    template,
    setTemplate,
    subject,
    setSubject,
    preheader,
    setPreheader,
    testModalOpen,
    setTestModalOpen,
    tab,
    setTab,
    errors,
    setErrors,
    sampleFanId,
    setSampleFanId,
    sampleData,
    setSampleData,
    scheduledSend,
    setScheduledSend,
    scheduledSendPaneOpen,
    setScheduledSendPaneOpen,
    timeZone,
    setTimeZone,
    updateBroadcast,
    waitForBroadcastSaves,
    autosaveTemplate,
    isAutosaving,
    cancelAutosave,
    parseLiquid,
    sendDomain,
    availableSendDomains,
    emailSendDomain,
    setEmailSendDomain,
    selectedSendDomain,
    setSelectedSendDomain,
    beefreeTemplate,
    setBeefreeTemplate,
    previouslySent,
    sentAt,
    overviewMetrics,
    overviewLinkClicks,
    ahoyMessagesDataTableProps,
    segmentFilters,
    setSegmentFilters,
    visibleSegmentFilters,
    createSegmentFilter,
    updateSegmentFilter,
    destroySegmentFilter,
    fetchPreviousBroadcastTemplates,
    previousBroadcastTemplates,
    fetchAdminBlueprints,
    adminBlueprints,
    fetchTeamBlueprints,
    teamBlueprints,
    createBlueprint,
    updateBlueprint,
    previewTemplate,
    setPreviewTemplate,
    fetchRecipientCount,
    recipientsCount,
    setRecipientsCount,
    checkTemplateHasContent,
    templateHasContent,
    setTemplateHasContent,
    scheduleBroadcast,
    scheduledAt,
    setScheduledAt,
    processedTemplate,
    setProcessedTemplate,
    segment,
    setSegment,
    fanFilterGroupId,
    setFanFilterGroupId,
    formatDate,
    twilioMessagingServices,
    twilioTemplate,
    setTwilioTemplate,
    showTwilioComposer,
    setShowTwilioComposer,
    sampleTwilioData,
    emailMergeTags,
    twilioMergeTags,
    twilioEventMergeTags,
    smsStatus,
    smsStarterTexts,
    smsStarterPlaceholders,
    aiSegmentBuilder: props.aiSegmentBuilder || { enabled: false, examples: [] },
  };

  return (
    <BroadcastEditContext.Provider value={value}>
      {props.children}
    </BroadcastEditContext.Provider>
  );
};

const BroadcastEditContainer = (props) => (
  <BroadcastEditProvider {...props}>
    <ToastContainer icon={false} />
    <BroadcastEdit />
  </BroadcastEditProvider>
);

export default BroadcastEditContainer;
